import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import {
  isIosDevice,
  isIosNonSafariBrowser,
  isStandalonePwa,
} from '../../push/webPush';
import { STORAGE_KEYS } from '../../utils/constants';
import { useAuth } from '../../hooks/useAuth';

const SAFARI_STEPS = [
  'Tap the Share button at the bottom of Safari (square with an arrow).',
  'Scroll down and tap Add to Home Screen.',
  'Tap Add, then open Prasaar from your home screen (not Safari).',
  'Sign in → Profile → Enable push notifications.',
];

const CHROME_STEPS = [
  'Copy this page URL from the address bar.',
  'Open Safari on your iPhone and paste the URL.',
  'Tap Share → Add to Home Screen → Add.',
  'Open the Prasaar home screen icon → Profile → Enable push notifications.',
];

function shouldShowIosSetup() {
  if (typeof window === 'undefined') return false;
  if (!isIosDevice()) return false;
  if (isStandalonePwa()) return false;
  return localStorage.getItem(STORAGE_KEYS.IOS_PWA_SETUP_SEEN) !== '1';
}

export function IosPwaSetupDialog() {
  const { user } = useAuth();
  const iosChrome = useMemo(() => isIosNonSafariBrowser(), []);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (user && shouldShowIosSetup()) {
      setOpen(true);
    }
  }, [user]);

  useEffect(() => {
    function onAsk() {
      setOpen(true);
    }
    window.addEventListener('prasaar-ios-setup', onAsk);
    return () => window.removeEventListener('prasaar-ios-setup', onAsk);
  }, []);

  function dismiss() {
    localStorage.setItem(STORAGE_KEYS.IOS_PWA_SETUP_SEEN, '1');
    setOpen(false);
  }

  if (!isIosDevice() || isStandalonePwa()) return null;

  const steps = iosChrome ? CHROME_STEPS : SAFARI_STEPS;

  return (
    <Dialog
      open={open}
      onClose={dismiss}
      fullWidth
      maxWidth="xs"
      aria-labelledby="ios-pwa-setup-title"
    >
      <DialogTitle id="ios-pwa-setup-title" sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PhoneIphoneIcon color="secondary" />
          <span>iPhone setup</span>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Typography color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
          {iosChrome
            ? 'Chrome on iPhone cannot install a full PWA. Use Safari once to add Prasaar to your home screen for reliable alerts.'
            : 'Install Prasaar on your home screen so iOS can deliver system notifications when the app is closed.'}
        </Typography>
        <List dense disablePadding>
          {steps.map((step, index) => (
            <ListItem key={step} disableGutters sx={{ alignItems: 'flex-start', py: 0.75 }}>
              <ListItemText
                primary={`${index + 1}. ${step}`}
                primaryTypographyProps={{ variant: 'body2', lineHeight: 1.55 }}
              />
            </ListItem>
          ))}
        </List>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          After it is on your home screen, open Prasaar from there and turn on
          phone notifications in Profile.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="contained" color="secondary" onClick={dismiss}>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
}
